import React from 'react';
import { Grid } from '@mui/material';
import { FormInlineInput } from 'ui-component/formComponents/Sections/FormInlineInput';
import { FieldDataTypeEnum } from 'ui-component/records/types';
import { getEditableField, NON_EDITABLE_FIELDS } from 'ui-component/records/utils';
import { getDropdownOptionList } from 'ui-component/RecordView/utils';
import { FIELD_CONDITIONAL_THEN_OPERATOR_LIST, FieldConditionalThenOperatorEnum } from 'views/DataFocusView/types';

type SectionFieldProps = {
    filteredField: any;
    section: any;
    headers: any;
    propertiesById: any;
    listValuesByListId: any;
    recordStatusList: any;
    problemCodeList: any;
    usersList: any;
    targetTenantList: any;
    fieldAccessList: any;
    fullfilledConditionsIds?: string[];
    activeDataFocusView?: boolean;
    loadingListValues?: boolean;
    inputRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
    getFieldThenOrOtherwiseConditions: (fieldName: string, objectPropertyName?: string) => any;
    nestedField?: boolean;
    recordId?: string;
    recordTypeId?: string;
};

const SectionField = ({
    recordId,
    recordTypeId,
    filteredField,
    section,
    headers,
    propertiesById,
    listValuesByListId,
    recordStatusList,
    problemCodeList,
    usersList,
    targetTenantList,
    fieldAccessList,
    fullfilledConditionsIds,
    activeDataFocusView,
    loadingListValues,
    inputRefs,
    getFieldThenOrOtherwiseConditions,
    nestedField
}: SectionFieldProps) => {
    const { name, baseField, recordAdditionalFieldsByType, objectProperty, isRequired, defaultValue, nestedFieldPath } = filteredField;

    const isField = !!baseField || !!recordAdditionalFieldsByType;
    const fieldDef = headers[baseField || recordAdditionalFieldsByType?.name];
    const propertyDef = objectProperty ? propertiesById[+objectProperty.id] : undefined;

    const additionalFieldNameByObjectId = Object.keys(headers).find(
        (key) => headers[key].objectDefinition?.id === Number(propertyDef?.objectDefinition?.id)
    );

    // eslint-disable-next-line no-nested-ternary
    const inputName = isField
        ? nestedField
            ? `${nestedFieldPath}.${fieldDef?.fieldName}`
            : fieldDef?.fieldName
        : nestedField
        ? `${nestedFieldPath}.${additionalFieldNameByObjectId}.${propertyDef?.name}`
        : `${additionalFieldNameByObjectId}.${propertyDef?.name}`;
    const listTypeId = fieldDef?.listType?.id;

    const options = listTypeId
        ? listValuesByListId[+listTypeId]
        : getDropdownOptionList(baseField as any, [], recordStatusList, problemCodeList, usersList, targetTenantList);

    const cleanDefaultValue =
        defaultValue === '""' || defaultValue === '""' ? '' : defaultValue?.replace(/"/g, '').replace(/"/g, '').replace(/\\/g, '');

    const ACKFieldConfiguration = {
        isACKField: JSON.parse(filteredField.specialValue ?? '{}')?.value === 'ACK',
        recordTypeId: isNaN(Number(filteredField.defaultValue?.replaceAll('"', '').replaceAll('\\', '')))
            ? 0
            : Number(filteredField.defaultValue?.replaceAll('"', '').replaceAll('\\', '')),
        fieldTargetId: filteredField.recordAdditionalFieldsByType?.id || '',
        headers
    };

    const usedConditions = getFieldThenOrOtherwiseConditions(
        isField ? fieldDef?.fieldName || '' : propertyDef?.objectDefinition?.name || '',
        propertyDef?.name
    );

    const usedAndFullfiledConditions = usedConditions?.filter((el: any) => fullfilledConditionsIds?.includes(el.id)) || [];
    const usedAndNonFullfiledConditions = usedConditions?.filter((el: any) => !fullfilledConditionsIds?.includes(el.id)) || [];

    const actionsByConditions: (typeof FIELD_CONDITIONAL_THEN_OPERATOR_LIST)[FieldConditionalThenOperatorEnum][] = [];

    for (const { then } of usedAndFullfiledConditions) {
        const thenAppliedToThisField = then.filter((el: any) => {
            const isProperty = !!propertyDef?.name;
            if (!isProperty) {
                return el.field === fieldDef?.fieldName || '';
            }
            return false;
        });

        actionsByConditions.push(
            ...thenAppliedToThisField.map(
                (el: any) => FIELD_CONDITIONAL_THEN_OPERATOR_LIST[el.operator as keyof typeof FIELD_CONDITIONAL_THEN_OPERATOR_LIST]
            )
        );
    }

    for (const { otherwise } of usedAndNonFullfiledConditions) {
        const otherwiseAppliedToThisField = otherwise.field === fieldDef?.fieldName || '';
        if (otherwiseAppliedToThisField)
            actionsByConditions.push(
                FIELD_CONDITIONAL_THEN_OPERATOR_LIST[otherwise.operator as keyof typeof FIELD_CONDITIONAL_THEN_OPERATOR_LIST]
            );
    }

    if (actionsByConditions.includes(FIELD_CONDITIONAL_THEN_OPERATOR_LIST.HIDDEN)) return null;

    return (
        <Grid item xs={section.columns} id={`${section.id}-${name}`}>
            {isField && (
                <FormInlineInput
                    ACKFieldConfiguration={ACKFieldConfiguration}
                    label={name}
                    isFormField
                    options={options}
                    name={inputName || ''}
                    fieldDef={fieldDef}
                    disabled={
                        !getEditableField(fieldDef?.fieldName || '', fieldAccessList) ||
                        NON_EDITABLE_FIELDS.includes(fieldDef?.fieldName as (typeof NON_EDITABLE_FIELDS)[number])
                    }
                    isCustomList={!!listTypeId}
                    isRequired={(fieldDef?.required || isRequired) && getEditableField(fieldDef?.fieldName || '', fieldAccessList)}
                    activeDataFocusView={activeDataFocusView}
                    noWrap
                    defaultValue={cleanDefaultValue}
                    loadingListValues={loadingListValues}
                    inputRefs={inputRefs}
                    showFieldMenu={false}
                    recordId={recordId}
                    recordTypeId={recordTypeId}
                />
            )}

            {propertyDef && (
                <FormInlineInput
                    label={name}
                    isFormField
                    options={options}
                    fieldDef={{
                        dataType: propertyDef.dataType as FieldDataTypeEnum,
                        id: propertyDef.id,
                        description: '',
                        gridEditable: false,
                        required: propertyDef.isRequired,
                        headerField: false,
                        visibility: 'standard'
                    }}
                    name={inputName || ''}
                    isCustomList={false}
                    isRequired={propertyDef?.isRequired || isRequired}
                    noWrap
                    isObjectProperty
                    defaultValue={cleanDefaultValue}
                    activeDataFocusView={activeDataFocusView}
                    loadingListValues={loadingListValues}
                    inputRefs={inputRefs}
                    showFieldMenu={false}
                    recordId={recordId}
                    recordTypeId={recordTypeId}
                />
            )}
        </Grid>
    );
};

export default SectionField;
